import { AbilityBuilder, Ability } from "@casl/ability";
import {
  addEntityRules,
  resetEntitiesAccess,
  Rights,
} from "@/config/entityRules.js";
import Store from "@/store";

function processRules() {
  const abilityBuilder = new AbilityBuilder(Ability);
  resetEntitiesAccess();

  if (Store.getters["Auth/isSuperAdmin"]) {
    addEntityRules(
      abilityBuilder,
      "Customer",
      Rights.CanManage | Rights.CanRead | Rights.CanCreate | Rights.CanEdit
    );
    addEntityRules(
      abilityBuilder,
      "Site",
      Rights.CanManage | Rights.CanRead | Rights.CanCreate | Rights.CanEdit
    );

    addEntityRules(abilityBuilder, "SiteUnit", Rights.CanDoEverything);
    addEntityRules(
      abilityBuilder,
      "SiteUnitAssignment",
      Rights.CanDoEverything
    );
    addEntityRules(abilityBuilder, "License", Rights.CanDoEverything);
    addEntityRules(abilityBuilder, "MindPodLicense", Rights.CanDoEverything);
    addEntityRules(abilityBuilder, "PatientLicense", Rights.CanDoEverything);
    addEntityRules(abilityBuilder, "Asset", Rights.CanDoEverything);
    addEntityRules(abilityBuilder, "UserSession", Rights.CanRead);
    addEntityRules(
      abilityBuilder,
      "SiteManagerProfile",
      Rights.CanDoEverything
    );
    addEntityRules(abilityBuilder, "TherapistProfile", Rights.CanDoEverything);
    addEntityRules(abilityBuilder, "AdminProfile", Rights.CanDoEverything);
    addEntityRules(abilityBuilder, "DpoProfile", Rights.CanDoEverything);
    addEntityRules(abilityBuilder, "PatientProfile", Rights.CanRead);
    abilityBuilder.can("edit", "PatientProfile", [
      "isArchived",
      "isAccountDeactivated",
    ]);
    addEntityRules(
      abilityBuilder,
      "CustomerNotification",
      Rights.CanDoEverything
    );
    addEntityRules(
      abilityBuilder,
      "OptionalFeatureDefinition",
      Rights.CanDoEverything
    );
    addEntityRules(
      abilityBuilder,
      "OptionalFeatureActivation",
      Rights.CanDoEverything
    );
    addEntityRules(
      abilityBuilder,
      "FeedbackReport",
      Rights.CanRead | Rights.CanCreate
    );
    addEntityRules(abilityBuilder, "FeedbackReportAttachment", Rights.CanRead);
    addEntityRules(abilityBuilder, "AuditLog", Rights.CanRead);
  } else if (Store.getters["Auth/isAdmin"]) {
    // Default Admin without any specific competency
    addEntityRules(abilityBuilder, "Customer", Rights.CanRead);
    addEntityRules(abilityBuilder, "Site", Rights.CanRead);
    addEntityRules(abilityBuilder, "SiteUnit", Rights.CanRead);
    addEntityRules(abilityBuilder, "SiteUnitAssignment", Rights.CanRead);
    addEntityRules(abilityBuilder, "License", Rights.CanRead);
    addEntityRules(abilityBuilder, "MindPodLicense", Rights.CanRead);
    addEntityRules(abilityBuilder, "PatientLicense", Rights.CanRead);
    addEntityRules(abilityBuilder, "Asset", Rights.CanRead);
    addEntityRules(abilityBuilder, "SiteManagerProfile", Rights.CanRead);
    addEntityRules(abilityBuilder, "TherapistProfile", Rights.CanRead);
    addEntityRules(abilityBuilder, "AdminProfile", Rights.CanRead);
    addEntityRules(abilityBuilder, "DpoProfile", Rights.CanRead);
    addEntityRules(abilityBuilder, "OptionalFeatureDefinition", Rights.CanRead);
    addEntityRules(abilityBuilder, "OptionalFeatureActivation", Rights.CanRead);
    addEntityRules(abilityBuilder, "FeedbackReport", Rights.CanRead);
    addEntityRules(abilityBuilder, "FeedbackReportAttachment", Rights.CanRead);

    // Admin account manager competency
    if (Store.getters["Auth/isAdminManagerAdmin"]) {
      addEntityRules(abilityBuilder, "SiteManagerProfile", Rights.CanRead);
      addEntityRules(abilityBuilder, "TherapistProfile", Rights.CanRead);
      addEntityRules(abilityBuilder, "AdminProfile", Rights.CanDoEverything);
      addEntityRules(abilityBuilder, "DpoProfile", Rights.CanDoEverything);
    }

    // Account executive admin
    if (Store.getters["Auth/isAccountExecutiveAdmin"]) {
      addEntityRules(abilityBuilder, "Site", Rights.CanDoEverything);
      addEntityRules(abilityBuilder, "SiteUnit", Rights.CanDoEverything);
      addEntityRules(
        abilityBuilder,
        "SiteUnitAssignment",
        Rights.CanDoEverything
      );
      addEntityRules(abilityBuilder, "Asset", Rights.CanDoEverything);
      addEntityRules(
        abilityBuilder,
        "SiteManagerProfile",
        Rights.CanDoEverything
      );
      addEntityRules(
        abilityBuilder,
        "TherapistProfile",
        Rights.CanDoEverything
      );
      addEntityRules(abilityBuilder, "AdminProfile", Rights.CanDoEverything);
      addEntityRules(abilityBuilder, "DpoProfile", Rights.CanDoEverything);
      addEntityRules(abilityBuilder, "Customer", Rights.CanEdit);

      abilityBuilder.cannot("edit", "TherapistProfile", ["userName"]);
    }

    // Data scientist
    if (Store.getters["Auth/isDataScientistAdmin"]) {
      abilityBuilder.can("edit", "Site", ["kind"]);
    }

    // Publisher
    if (Store.getters["Auth/isPublisherAdmin"]) {
      // right now can't do anything more than default admin
    }

    // Data export manager
    if (Store.getters["Auth/isDataExportManagerAdmin"]) {
      // right now can't do anything more than default admin
    }
    // Optional feature manager
    if (Store.getters["Auth/isOptionalFeatureManagerAdmin"]) {
      addEntityRules(
        abilityBuilder,
        "OptionalFeatureActivation",
        Rights.CanDoEverything
      );
    }
    if (Store.getters["Auth/isLicenseManagerAdmin"]) {
      // License manager admin
      addEntityRules(abilityBuilder, "License", Rights.CanDoEverything);
      addEntityRules(abilityBuilder, "MindPodLicense", Rights.CanDoEverything);
      addEntityRules(abilityBuilder, "PatientLicense", Rights.CanDoEverything);
      addEntityRules(abilityBuilder, "Customer", Rights.CanCreate);
    }
  } else if (Store.getters["Auth/isDPO"]) {
    // DPO admin
    addEntityRules(abilityBuilder, "AuditLog", Rights.CanRead);
    addEntityRules(abilityBuilder, "Site", Rights.CanRead);
    addEntityRules(abilityBuilder, "SiteUnit", Rights.CanRead);
    addEntityRules(abilityBuilder, "SiteUnitAssignment", Rights.CanRead);
    addEntityRules(
      abilityBuilder,
      "SiteManagerProfile",
      Rights.CanManage | Rights.CanRead | Rights.CanDelete
    );
    addEntityRules(
      abilityBuilder,
      "TherapistProfile",
      Rights.CanManage | Rights.CanRead | Rights.CanDelete
    );
    abilityBuilder.can("edit", "TherapistProfile", ["userName", "password"]);
    addEntityRules(abilityBuilder, "AdminProfile", Rights.CanDoEverything);
    addEntityRules(abilityBuilder, "AdminProfile", Rights.CanEdit);
    abilityBuilder.cannot("edit", "AdminProfile", ["email"]);
    addEntityRules(
      abilityBuilder,
      "DpoProfile",
      Rights.CanManage | Rights.CanRead | Rights.CanCreate | Rights.CanDelete
    );
    addEntityRules(
      abilityBuilder,
      "PatientProfile",
      Rights.CanManage | Rights.CanRead | Rights.CanDelete
    );
    abilityBuilder.can("edit", "PatientProfile", [
      "isArchived",
      "isAccountDeactivated",
      "password",
    ]);
    addEntityRules(abilityBuilder, "OptionalFeatureDefinition", Rights.CanRead);
    addEntityRules(abilityBuilder, "OptionalFeatureActivation", Rights.CanRead);
  } else if (Store.getters["Auth/isSiteManager"]) {
    // Site manager
    addEntityRules(abilityBuilder, "Site", Rights.CanRead);
    addEntityRules(abilityBuilder, "SiteUnit", Rights.CanDoEverything);
    addEntityRules(
      abilityBuilder,
      "SiteUnitAssignment",
      Rights.CanDoEverything
    );
    abilityBuilder.can("edit", "Site", ["passwordParameters"]);
    abilityBuilder.can("edit", "Site", ["timeZoneId"]);
    abilityBuilder.can("edit", "Site", ["countryCode"]);
    abilityBuilder.can("edit", "Site", ["samlParameters"]);
    abilityBuilder.can("edit", "Site", ["smartParameters"]);

    addEntityRules(
      abilityBuilder,
      "SiteManagerProfile",
      Rights.CanRead | Rights.CanEdit
    );
    addEntityRules(
      abilityBuilder,
      "TherapistProfile",
      Rights.CanRead | Rights.CanEdit
    );
  } else if (Store.getters["Auth/isTherapist"]) {
    // Therapist
    addEntityRules(
      abilityBuilder,
      "PatientProfile",
      Rights.CanCreate | Rights.CanRead | Rights.CanEdit
    );
    addEntityRules(abilityBuilder, "Site", Rights.CanRead);
    addEntityRules(
      abilityBuilder,
      "VideoMeeting",
      Rights.CanRead | Rights.CanCreate | Rights.CanEdit | Rights.CanDelete
    );
    addEntityRules(abilityBuilder, "SiteUnit", Rights.CanRead);
    addEntityRules(abilityBuilder, "SiteUnitAssignment", Rights.CanRead);
    addEntityRules(abilityBuilder, "UserSession", Rights.CanCreate);
    abilityBuilder.cannot("edit", "PatientProfile", [
      "isArchived",
      "isAccountDeactivated",
    ]);

    // Therapist admin
    if (Store.getters["Auth/isSuperTherapist"]) {
      addEntityRules(
        abilityBuilder,
        "SiteManagerProfile",
        Rights.CanRead | Rights.CanEdit
      );
      addEntityRules(
        abilityBuilder,
        "TherapistProfile",
        Rights.CanRead | Rights.CanEdit
      );

      addEntityRules(
        abilityBuilder,
        "SiteUnit",
        Rights.CanRead | Rights.CanEdit
      );
      addEntityRules(
        abilityBuilder,
        "SiteUnitAssignment",
        Rights.CanDoEverything
      );
      abilityBuilder.cannot("edit", "Site", ["passwordParameters"]);
      abilityBuilder.can("edit", "Site", ["timeZoneId"]);
      abilityBuilder.can("edit", "Site", ["countryCode"]);
      // N.B: SAML Parameters edition depends also on the site.samlParameters.canSupertherapistAdministerSAML setting.
      abilityBuilder.can("edit", "Site", ["samlParameters"]);
      abilityBuilder.can("edit", "Site", ["smartParameters"]);

      // Can archive patients
      abilityBuilder.can("edit", "PatientProfile", [
        "isArchived",
        "isAccountDeactivated",
      ]);
    }
  } else if (Store.getters["Auth/isPatient"]) {
    // Patient
    addEntityRules(abilityBuilder, "VideoMeeting", Rights.CanRead);
    addEntityRules(abilityBuilder, "UserSession", Rights.CanCreate);
  }

  return abilityBuilder.rules;
}

let ability = new Ability();

const updateUserAccessRights = () => {
  if (ability) {
    ability.update(processRules());
  }
};

export { ability, updateUserAccessRights };
